/**
 * DevFixPanel - Main container for dev inspector UI
 * Shows in dev mode, or when localStorage.showDevFix === 'true'
 */

import { useState, useCallback } from 'react';
import { MessageSquare, X } from 'lucide-react';
import { ElementInspectorOverlay } from './ElementInspectorOverlay';
import { DevFixChat } from './DevFixChat';
import type { ElementMetadata } from '../../services/devfix/elementInspector';

export function DevFixPanel() {
  const [isInspectorActive, setInspectorActive] = useState(false);
  const [isChatOpen, setChatOpen] = useState(false);
  const [selectedElement, setSelectedElement] = useState<ElementMetadata | null>(null);

  // Always show DevFix panel (remove this check to hide in production)
  // To hide: set localStorage.setItem('hideDevFix', 'true')
  const shouldHide = typeof window !== 'undefined' && localStorage.getItem('hideDevFix') === 'true';

  if (shouldHide) {
    return null;
  }

  const handleElementSelected = useCallback((metadata: ElementMetadata) => {
    setSelectedElement(metadata);
    setInspectorActive(false);
    setChatOpen(true);
  }, []);

  const handleCloseChat = useCallback(() => {
    setChatOpen(false);
    setSelectedElement(null);
  }, []);

  return (
    <>
      {/* Floating Chat Button - bottom right */}
      <button
        onClick={() => setChatOpen(!isChatOpen)}
        title={isChatOpen ? 'Close DevFix' : 'Open DevFix'}
        style={{
          position: 'fixed',
          bottom: '100px',
          left: '20px',
          width: '56px',
          height: '56px',
          borderRadius: '50%',
          border: '2px solid #10b981',
          background: isChatOpen ? '#10b981' : 'white',
          color: isChatOpen ? 'white' : '#10b981',
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
          transition: 'all 0.2s',
          zIndex: 99999,
        }}
      >
        {isChatOpen ? <X size={24} /> : <MessageSquare size={24} />}
      </button>

      {/* Element Inspector Overlay */}
      {isInspectorActive && (
        <ElementInspectorOverlay
          onElementSelected={handleElementSelected}
          onCancel={() => setInspectorActive(false)}
        />
      )}

      {/* DevFix Chat */}
      {isChatOpen && (
        <DevFixChat
          selectedElement={selectedElement}
          onClose={handleCloseChat}
          onSelectElement={() => {
            setChatOpen(false);
            setInspectorActive(true);
          }}
        />
      )}
    </>
  );
}
